import { useState, ChangeEvent, useMemo } from 'react';
import {
  Modal,
  Tabs,
  Form,
  Input,
  Select,
  message,
  Button,
  Popconfirm,
  FormProps,
  Radio,
} from 'antd';
import Icon from '@ant-design/icons';
import { css } from '@emotion/css';
import useIMEComposition from 'hooks/useComposition';
import useAssistantGroup from 'hooks/business/useAssistantGroup';
import useAssistantList from 'hooks/business/useAssistantList';
import {
  batchTransferConversation,
  getTalkInfoWithPhone,
  transferConversation,
} from 'services/api/conversation.convList';
import { useDelConversationById, useMakeConversationCache } from 'hooks/business/conversation';
import { useRouteAccessProps } from 'hooks/business/useNewPermisson';
import useRoles from 'hooks/business/useRoles';
import {
  useBatchForwardEnable,
  useBatchForwardIsDelConv,
  useBatchForwardConvIds,
  useSetBatchForwardLoading,
  useSetBatchForwardIsDelConv,
} from 'hooks/business/conversationsHooks/useConversationBatchForward';
import styled from '@emotion/styled';
import { useAuthorization } from 'components/Authorization';
import { useTranslate } from '@tolgee/react';
import posthog from '@leyan/lytics';
import { ReactComponent as SvgClientSel } from './assets/clientSel.svg';
import { useConversationToggleTextareaProps } from 'hooks/business/conversationsHooks/useConversationHotKey';
import { useMemoizedFn, useUnmount } from 'ahooks';

const { TabPane } = Tabs;
const { Option } = Select;

const cssForward = css`
  .ant-modal-body {
    padding: 0;
  }
  .ant-tabs-nav {
    padding: 0 24px;
  }
  .ant-tabs-tab {
    padding: 17px 0;
  }
  .ant-tabs-tab-btn {
    color: #515359;
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--fett-app-primary-color);
  }
  .ant-form {
    margin: 0 24px;
  }
  .ant-tabs-ink-bar {
    background: var(--fett-app-primary-color);
  }
  .forwardNote {
    position: relative;
  }
  .forwardNoteCount {
    display: inline-block;
    position: absolute;
    bottom: 8px;
    right: 16px;
    color: #84868c;
  }
  .ant-popover-disabled-compatible-wrapper {
    margin-left: 8px;
  }
`;
const cssForwardSelect = css`
  .ant-select-item-option-content {
    display: flex;
    align-items: center;
    .forwardClient {
      color: #515359;
      width: 120px;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      margin-right: 12px;
    }
    .forwardOnline {
      color: #232426;
      font-weight: normal;
    }
    .forwardOnlineIcon {
      background-color: #00bf69;
      border-radius: 4px;
      width: 8px;
      height: 8px;
      margin-right: 4px;
    }
    .forwardOffline {
      font-weight: normal;
      color: #84868c;
    }
    .forwardOfflineIcon {
      background-color: #84868c;
      border-radius: 4px;
      width: 8px;
      height: 8px;
      margin-right: 4px;
    }
    .forwardTodeal {
      margin-left: 20px;
      border: 1px solid #b5b9bf;
      padding: 0 8px;
      color: #515359;
      font-size: 12px;
      flex: 1;
      font-weight: normal;
    }
    .iconSel {
      margin-right: 4px;
      visibility: hidden;
    }
  }
  .ant-select-item-option-selected {
    background-color: #fff;
    .forwardClient {
      color: #336fff !important;
    }
    .iconSel {
      visibility: visible;
    }
  }
  .ant-select-item-option {
    padding: 5px 24px;
  }
`;
const BatchForwardTotal = styled.span`
  padding: 0 8px;
`;

type transferType = 'client' | 'group';
type FieldsChangeParams = Parameters<NonNullable<FormProps['onFieldsChange']>>;

interface IFormValues {
  toId: string;
  remark?: string;
  deleteTalk?: boolean;
}

interface Props {
  /** 单个会话转发 */
  conversation?: Conversation.Conversation;
  onClose?: () => void;
}

let batchForwardTimer: any;

const clearBatchForwardTimer = () => {
  if (!batchForwardTimer) return;

  clearTimeout(batchForwardTimer);
  batchForwardTimer = null;
};

function ForwardDlg({ conversation, onClose }: Props) {
  const { newPermissionEnable } = useRouteAccessProps();
  const { t } = useTranslate();
  const { assistantGroup, fetchAssistantGroup } = useAssistantGroup();
  const { assistantList, fetchAssistantList } = useAssistantList();

  const { isSuperviser } = useRoles();
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const [okBtnDisable, setOkBtnDisable] = useState(true);

  // 批量转发
  const batchForwardEnable = useBatchForwardEnable();
  const batchForwardConvIds = useBatchForwardConvIds();
  const batchForwardIsDelConv = useBatchForwardIsDelConv();
  const setBatchForwardIsDelConv = useSetBatchForwardIsDelConv();
  const setBatchForwardLoading = useSetBatchForwardLoading();
  const delConversationById = useDelConversationById();
  const setConversation = useMakeConversationCache('set');

  /** 转发表单 ========== */
  const [formClient] = Form.useForm();
  const [formGroup] = Form.useForm();
  const tFrom = { client: formClient, group: formGroup };

  const [transferType, setTransferType] = useState<transferType>('client');
  const [bothFill, setBothFill] = useState(false);

  const chgTypeOfTransfer = useMemoizedFn((key: string) => {
    setTransferType(key as transferType);
    const form = tFrom[key as transferType];
    form.validateFields().catch(() => {
      setOkBtnDisable(true);
    });
  });

  const closeDlg = useMemoizedFn(() => {
    setBothFill(false);
    setOkBtnDisable(true);
    onClose?.();
    tFrom.client.resetFields();
    tFrom.group.resetFields();
  });

  const fetchSingleTransfer = useMemoizedFn(async (values: IFormValues) => {
    if (!conversation) return;

    const { conversationId, talkId } = conversation;
    const d: ApiConversation.TransferConversationParam = {
      talkId,
      conversationId,
      remark: values.remark,
      deleteTalk: values.deleteTalk,
      [transferType === 'client' ? 'toId' : 'toGroupId']: values.toId,
    };

    try {
      await transferConversation(d);
      closeDlg();
      if (values.deleteTalk) {
        delConversationById(conversationId);
      } else {
        const res = await getTalkInfoWithPhone({ conversationId, needPhone: true });
        setConversation(res);
      }
    } catch (error) {
      message.error(t('api.transferConversationErr'));
    }
  });

  const fetchBatchTransfer = useMemoizedFn((values: IFormValues) => {
    if (payload?.id) {
      batchTransferConversation({
        conversationId: batchForwardConvIds,
        userId: payload.id,
        remark: values.remark,
        deleteTalk: values.deleteTalk,
        toId: values.toId,
      })
        .then(() => {
          clearBatchForwardTimer();
          setBatchForwardLoading(true);
          closeDlg();
          // 超时处理
          batchForwardTimer = setTimeout(() => {
            setBatchForwardLoading(false);
            clearBatchForwardTimer();
          }, 20000);
          posthog.capture('$batchTransferConversation', {
            conversationIdList: batchForwardConvIds,
          });
        })
        .catch(() => {
          setBatchForwardLoading(false);
          message.error(t('api.common.error.messages', { action: t('api.common.action.request') }));
        });
    }
  });

  const doTransfer = useMemoizedFn(async (bothFillConfirm: boolean = false) => {
    let bothFillValid = true;
    try {
      const values = await Promise.all(Object.keys(tFrom).map((k) => tFrom[k].validateFields()));
      bothFillValid = values.every((v) => v.toId);
    } catch {
      bothFillValid = false;
    }
    if (bothFillValid && !bothFillConfirm) {
      setBothFill(true);
      return;
    }
    const form = tFrom[transferType];
    form
      .validateFields()
      .then(async (values: IFormValues) => {
        if (batchForwardEnable) {
          fetchBatchTransfer(values);
        } else {
          fetchSingleTransfer(values);
        }
      })
      .catch(() => {});
  });

  const maxCount = 100;
  const [inputCountClient, setInputCountClient] = useState(0);
  const [inputCountServer, setInputCountServer] = useState(0);
  const onChange = useMemoizedFn((evt: ChangeEvent<HTMLTextAreaElement>) => {
    if (!isOnIMEComposition) {
      if (transferType === 'client') {
        setInputCountClient(evt.target.value.length);
      } else {
        setInputCountServer(evt.target.value.length);
      }
    }
  });
  const [isOnIMEComposition, handleIMEComposition] = useIMEComposition((value) => {
    if (transferType === 'client') {
      setInputCountClient(value.length);
    } else {
      setInputCountServer(value.length);
    }
  });

  const conversationToggleTextAreaProps = useConversationToggleTextareaProps();

  const ForwardNote = () => (
    <Form.Item label={t('forward.note')} name="remark" rules={[{ type: 'string', max: maxCount }]}>
      <div className="forwardNote">
        <Input.TextArea
          onCompositionStart={handleIMEComposition}
          onCompositionUpdate={handleIMEComposition}
          onCompositionEnd={handleIMEComposition}
          onChange={onChange}
          autoSize={{ minRows: 5, maxRows: 10 }}
          {...conversationToggleTextAreaProps}
        />
        <span className="forwardNoteCount">
          {transferType === 'client' ? inputCountClient : inputCountServer}/{maxCount}
        </span>
      </div>
    </Form.Item>
  );
  const ForwardAndDel = () =>
    isSuperviser ? null : (
      <Form.Item label={t('forward.isBatchAndDel')} name="deleteTalk">
        <Radio.Group
          onChange={(e) => {
            setBatchForwardIsDelConv(e.target.checked);
          }}
        >
          <Radio value>{t('forward.yes')}</Radio>
          <Radio value={false}>{t('forward.no')}</Radio>
        </Radio.Group>
      </Form.Item>
    );

  const initialValues = useMemo(
    () => ({
      toId: '',
      deleteTalk: batchForwardIsDelConv,
    }),
    [batchForwardIsDelConv],
  );

  const onFieldsChange = useMemoizedFn(
    (changedFields: FieldsChangeParams[0], allFields: FieldsChangeParams[1]) => {
      if (changedFields.length === 0) {
        setOkBtnDisable(true);
        return;
      }
      const err = allFields.some((v) => {
        if (v.errors && v.errors.length > 0) {
          return true;
        }
        return false;
      });
      setOkBtnDisable(err);
    },
  );

  const assistantOptions = useMemo(() => {
    return assistantList
      .filter(
        ({ assistantId }) => !(conversation?.receptionId === assistantId && !batchForwardEnable),
      )
      .map((v) => {
        let iconClassName = 'forwardOnlineIcon';
        let statusClassName = 'forwardOnline';
        let statusTip = t('forward.online');
        let unDealedConvNum = `${t('header.pending')} (${v.unDealedConvNum})`;
        let disabled = false;
        // 'ONLINE' | 'OFFLINE' | 'HANGUP'
        if (v.status === 'OFFLINE' || v.status === 'HANGUP') {
          disabled = true;
          iconClassName = 'forwardOfflineIcon';
          statusClassName = 'forwardOffline';
          statusTip = v.status === 'OFFLINE' ? t('forward.offline') : t('forward.hangup');
          unDealedConvNum = '';
        }
        return {
          ...v,
          disabled,
          iconClassName,
          statusClassName,
          statusTip,
          unDealedConvNum,
        };
      });
  }, [assistantList, batchForwardEnable, conversation?.receptionId, t]);

  const handleAssistantDropdownVisibleChange = useMemoizedFn(async (open) => {
    if (!open) {
      return;
    }
    fetchAssistantList().catch(() => {
      message.error(t('api.assistantListOnlineStatusStatusErr'));
    });
  });

  const assistantGroupOptions = useMemo(() => {
    return assistantGroup.map((v) => {
      let iconClassName = 'forwardOnlineIcon';
      let statusClassName = 'forwardOnline';
      // forward.online 是翻译文件中的值，被定义成const了，这里需要强制类型转换成sting,否则ts类型推导会报错。
      let statusTip = t('forward.online');
      let onlineAssistant = `${t('forward.onlineUsers')} (${v.onlineAssistantNum})`;
      let disabled = false;
      if (v.status === 'ASSISTANT_GROUP_OFFLINE' || v.status === 'ASSISTANT_GROUP_HANGUP') {
        disabled = true;
        iconClassName = 'forwardOfflineIcon';
        statusClassName = 'forwardOffline';
        statusTip =
          v.status === 'ASSISTANT_GROUP_OFFLINE' ? t('forward.offline') : t('forward.hangup');
        onlineAssistant = '';
      }
      return {
        ...v,
        iconClassName,
        statusClassName,
        statusTip,
        onlineAssistant,
        disabled,
      };
    });
  }, [assistantGroup, t]);

  const handleAssistantGroupDropdownVisibleChange = useMemoizedFn(async (open) => {
    if (!open) {
      return;
    }
    fetchAssistantGroup().catch(() => {
      message.error(t('api.assistantGroupOnlineStatusErr'));
    });
  });

  const filterSelect = useMemoizedFn((input: string, option: any) => {
    return (option?.label as string).toLowerCase().includes(input.toLowerCase());
  });

  const formJsx = (type: transferType) =>
    type === 'client' ? (
      <Form
        form={formClient}
        initialValues={initialValues}
        onFieldsChange={onFieldsChange}
        layout="vertical"
      >
        <Form.Item
          label={t('forward.clientSel')}
          name="toId"
          rules={[
            {
              required: true,
              message: t('forward.clientTip'),
            },
          ]}
        >
          <Select
            dropdownClassName={cssForwardSelect}
            allowClear
            optionLabelProp="label"
            showSearch
            filterOption={filterSelect}
            onDropdownVisibleChange={handleAssistantDropdownVisibleChange}
          >
            {assistantOptions.map(
              ({
                disabled,
                assistantId,
                assistantName,
                iconClassName,
                statusClassName,
                statusTip,
                unDealedConvNum,
              }) => {
                return (
                  <Option
                    disabled={disabled}
                    key={assistantId}
                    value={assistantId}
                    label={assistantName}
                  >
                    <span className="iconSel">
                      <Icon component={SvgClientSel} />
                    </span>
                    <span className="forwardClient">{assistantName}</span>
                    <span className={iconClassName} />
                    <span className={statusClassName}>{statusTip}</span>
                    {unDealedConvNum ? (
                      <span className="forwardTodeal">{unDealedConvNum}</span>
                    ) : null}
                  </Option>
                );
              },
            )}
          </Select>
        </Form.Item>

        {ForwardNote()}
        {ForwardAndDel()}
      </Form>
    ) : (
      <Form
        form={formGroup}
        initialValues={initialValues}
        onFieldsChange={onFieldsChange}
        layout="vertical"
      >
        <Form.Item
          label={t('forward.clientGroupSel')}
          name="toId"
          rules={[
            {
              required: true,
              message: t('forward.clientGroupTip'),
            },
          ]}
        >
          <Select
            dropdownClassName={cssForwardSelect}
            allowClear
            defaultValue={['']}
            optionLabelProp="label"
            showSearch
            filterOption={filterSelect}
            onDropdownVisibleChange={handleAssistantGroupDropdownVisibleChange}
          >
            {assistantGroupOptions.map(
              ({
                disabled,
                assistantGroupId,
                assistantGroupName,
                iconClassName,
                statusClassName,
                statusTip,
                onlineAssistant,
              }) => {
                return (
                  <Option
                    disabled={disabled}
                    key={assistantGroupId}
                    value={assistantGroupId}
                    label={assistantGroupName}
                  >
                    <span className="iconSel">
                      <Icon component={SvgClientSel} />
                    </span>
                    <span className="forwardClient">{assistantGroupName}</span>
                    <span className={iconClassName} />
                    <span className={statusClassName}>{statusTip}</span>
                    {onlineAssistant ? (
                      <span className="forwardTodeal">{onlineAssistant}</span>
                    ) : null}
                  </Option>
                );
              },
            )}
          </Select>
        </Form.Item>

        {ForwardNote()}
        {ForwardAndDel()}
      </Form>
    );

  useUnmount(() => {
    clearBatchForwardTimer();
  });

  return (
    <Modal
      className={cssForward}
      title={null}
      visible
      width={460}
      maskStyle={{ padding: 0 }}
      onCancel={closeDlg}
      footer={[
        batchForwardEnable ? (
          <BatchForwardTotal>
            {t('forward.batchTotalTips', { total: batchForwardConvIds.length })}
          </BatchForwardTotal>
        ) : null,
        <Button key="cancel" onClick={closeDlg}>
          {t('forward.btnCancel')}
        </Button>,
        <Popconfirm
          title={transferType === 'client' ? t('forward.toClientTip') : t('forward.toGroupTip')}
          visible={bothFill}
          onCancel={() => setBothFill(false)}
          onConfirm={() => {
            setBothFill(false);
            doTransfer(true);
          }}
        >
          <Button disabled={okBtnDisable} key="ok" type="primary" onClick={() => doTransfer(false)}>
            {t('forward.btnOk')}
          </Button>
        </Popconfirm>,
      ]}
    >
      <Tabs defaultActiveKey={transferType} onChange={chgTypeOfTransfer}>
        <TabPane tab={t('forward.client')} key="client">
          {formJsx('client')}
        </TabPane>
        {!newPermissionEnable && !batchForwardEnable && (
          <TabPane tab={t('forward.clientGroup')} key="group">
            {formJsx('group')}
          </TabPane>
        )}
      </Tabs>
    </Modal>
  );
}
export default ForwardDlg;
